import type { ApiResponse } from "@/features/auth/services/authApi";
import { apiClient } from "@/shared/services/api";

export interface Payment {
  id: string;
  orderId: string;
  orderNumber: string;
  customerName: string;
  customerPhone?: string | null;
  invoiceNumber?: string | null;
  mpesaReceiptNumber?: string | null;
  orderTotalAmount?: number | null;
  orderAllocatedAmount?: number | null;
  orderWalletCreditAmount?: number;
  orderPendingWalletCreditAmount?: number;
  paymentNumber: string;
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  paymentMethodAccountId?: string | null;
  paymentMethodAccountName?: string | null;
  paymentReference?: string | null;
  type: string;
  notes?: string | null;
  receiptNumber?: string | null;
  isReconciled: boolean;
  reconciledDate?: string | null;
  reconciledByUser?: string | null;
  refundForPaymentId?: string | null;
  refundReason?: string | null;
  isRefunded: boolean;
  refundedDate?: string | null;
  refundedAmount?: number | null;
  createdAt: string;
  createdBy: string;
  // Finance approval
  approvalStatus: string;
  approvedByUserId?: string | null;
  approvedByUserName?: string | null;
  approvedAt?: string | null;
  approvalNotes?: string | null;
  rejectionReason?: string | null;
  expoId?: string | null;
  expoName?: string | null;
  orderIsExpoSale?: boolean;

  /** Parent-order context — true when this payment belongs to a walk-in sale. Used by the
   * UI to gate the "Print receipt" action for split-tender walk-ins (print only once every
   * leg has been approved by Finance). */
  orderIsWalkIn?: boolean;
  /** True when the parent order's balance has reached zero — every payment leg has been
   * Finance-approved. For walk-in split-tender orders this is the moment the combined
   * receipt becomes available to print. */
  orderFullyPaid?: boolean;
}

export interface RecordPaymentRequest {
  orderId: string;
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  /** FK to a registered, GM-approved payment method. Required for new payments. */
  paymentMethodAccountId?: string;
  paymentReference?: string;
  notes?: string;
  /**
   * Required only when {@link amount} exceeds the order's outstanding balance.
   * Tells the till what to do with the surplus tender: numeric values mirror
   * the backend `SurplusDisposition` enum (1 = ReturnAsChange, 2 = CreditToWallet).
   * See SPEC_CUSTOMER_WALLET.
   */
  surplusDisposition?: 1 | 2;
}

export interface ReconcilePaymentRequest {
  paymentId: string;
  notes?: string;
}

export interface ProcessRefundRequest {
  paymentId: string;
  amount: number;
  reason: string;
  paymentMethod: string;
  paymentReference?: string;
  notes?: string;
}

export interface ApprovePaymentRequest {
  approvalNotes?: string;
}

export interface RejectPaymentRequest {
  rejectionReason: string;
}

export const PAYMENT_APPROVAL_STATUS = {
  PendingApproval: "PendingApproval",
  Approved: "Approved",
  Rejected: "Rejected",
  PartiallyApproved: "PartiallyApproved",
} as const;
export type PaymentApprovalStatus =
  (typeof PAYMENT_APPROVAL_STATUS)[keyof typeof PAYMENT_APPROVAL_STATUS];

/** Mirrors backend `ReceiptFormat`. Numeric to match the C# enum on the wire. */
export const RECEIPT_FORMAT = {
  Thermal80: 0,
  A4: 1,
} as const;
export type ReceiptFormat = (typeof RECEIPT_FORMAT)[keyof typeof RECEIPT_FORMAT];

export interface PrintPaymentReceiptRequest {
  isReprint: boolean;
  reprintReason?: string;
  ipAddress?: string;
  /** Defaults server-side to Thermal80 (cashier default). */
  format?: ReceiptFormat;
}

const unwrapResponse = <T>(data: ApiResponse<T> | T): T => {
  if (data && typeof data === "object" && "data" in (data as ApiResponse<T>)) {
    return (data as ApiResponse<T>).data;
  }

  return data as T;
};

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
}

export const paymentsApi = {
  getAllPayments: async (params: {
    pageNumber: number;
    pageSize: number;
    search?: string;
    paymentMethod?: string;
    fromDate?: string;
    toDate?: string;
    view?: "All" | "PendingApproval" | "Unreconciled" | "Refunds";
    paymentType?: "Payment" | "Refund";
  }): Promise<PagedResult<Payment>> => {
    const { data } = await apiClient.get<ApiResponse<PagedResult<Payment>>>("Payments", { params });
    return unwrapResponse<PagedResult<Payment>>(data);
  },

  recordPayment: async (payload: RecordPaymentRequest): Promise<Payment> => {
    const { data } = await apiClient.post<ApiResponse<Payment> | Payment>(
      "/Payments",
      payload
    );
    return unwrapResponse<Payment>(data);
  },

  getPaymentByNumber: async (paymentNumber: string): Promise<Payment> => {
    const { data } = await apiClient.get<ApiResponse<Payment> | Payment>(
      `/Payments/by-number/${paymentNumber}`
    );
    return unwrapResponse<Payment>(data);
  },

  getPaymentsByOrderId: async (orderId: string): Promise<Payment[]> => {
    const { data } = await apiClient.get<ApiResponse<Payment[]> | Payment[]>(
      `/Payments/order/${orderId}`
    );
    return unwrapResponse<Payment[]>(data);
  },

  getUnreconciledPayments: async (pageNumber = 1, pageSize = 20): Promise<PagedResult<Payment>> => {
    const { data } = await apiClient.get<ApiResponse<PagedResult<Payment>>>(
      "/Payments/unreconciled", { params: { pageNumber, pageSize } }
    );
    return unwrapResponse<PagedResult<Payment>>(data);
  },

  getRefundPayments: async (): Promise<Payment[]> => {
    const { data } = await apiClient.get<ApiResponse<Payment[]> | Payment[]>(
      "/Payments/refunds"
    );
    return unwrapResponse<Payment[]>(data);
  },

  reconcilePayment: async (
    paymentId: string,
    payload: ReconcilePaymentRequest
  ): Promise<void> => {
    await apiClient.post(`/Payments/${paymentId}/reconcile`, payload);
  },

  bulkReconcilePayments: async (paymentIds: string[]): Promise<void> => {
    await apiClient.post("/Payments/bulk-reconcile", paymentIds);
  },

  processRefund: async (payload: ProcessRefundRequest): Promise<Payment> => {
    const { data } = await apiClient.post<ApiResponse<Payment> | Payment>(
      "/Payments/refund",
      payload,
      { headers: { "Idempotency-Key": crypto.randomUUID() } }
    );
    return unwrapResponse<Payment>(data);
  },

  getPendingApprovalPayments: async (pageNumber = 1, pageSize = 20): Promise<PagedResult<Payment>> => {
    const { data } = await apiClient.get<ApiResponse<PagedResult<Payment>>>(
      "/Payments/pending-approval", { params: { pageNumber, pageSize } }
    );
    return unwrapResponse<PagedResult<Payment>>(data);
  },

  approvePayment: async (
    paymentId: string,
    payload: ApprovePaymentRequest
  ): Promise<Payment> => {
    const { data } = await apiClient.post<ApiResponse<Payment> | Payment>(
      `/Payments/${paymentId}/approve`,
      payload
    );
    return unwrapResponse<Payment>(data);
  },

  rejectPayment: async (
    paymentId: string,
    payload: RejectPaymentRequest
  ): Promise<Payment> => {
    const { data } = await apiClient.post<ApiResponse<Payment> | Payment>(
      `/Payments/${paymentId}/reject`,
      payload
    );
    return unwrapResponse<Payment>(data);
  },

  printReceipt: async (
    paymentId: string,
    payload: PrintPaymentReceiptRequest
  ): Promise<Blob> => {
    const { data } = await apiClient.post<Blob>(
      `/Payments/${paymentId}/receipt/print`,
      payload,
      {
        responseType: "blob",
        headers: {
          Accept: "application/pdf",
        },
      }
    );

    return data;
  },
};
